/**
 * Route chunks are loaded on demand (React.lazy in App.tsx), and every frontend
 * deploy prunes the previous build's hashed chunks from the bucket. A tab opened
 * before a deploy then fails to fetch the page it navigates to. Vite reports that
 * failure as a `vite:preloadError` event on window.
 *
 * Reloading picks up the new index.html and its chunks. The timestamp guard stops
 * a reload loop when the chunk is missing for another reason: a second failure
 * within the window is left to throw, and the ErrorBoundary shows it.
 */
export const STALE_CHUNK_RELOAD_KEY = 'staleChunkReloadAt';
export const STALE_CHUNK_RELOAD_WINDOW_MS = 10_000;

export function reloadOnStaleChunk(
  event: Event,
  storage: Storage = window.sessionStorage,
  reload: () => void = () => window.location.reload(),
  now: number = Date.now(),
): void {
  // Without storage there is no loop guard, so do not reload on our own. The
  // ErrorBoundary still offers a reload button (isChunkLoadError below).
  try {
    const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY) ?? 0);
    if (now - last < STALE_CHUNK_RELOAD_WINDOW_MS) {
      console.error('Chunk failed to load again after a reload', event);
      return;
    }
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  } catch (storageError) {
    console.error('Chunk failed to load; sessionStorage unavailable, not reloading', storageError);
    return;
  }
  // No preventDefault(): the import still rejects with its real error, and the
  // reload replaces the page before anyone reads the ErrorBoundary.
  reload();
}

/**
 * True for the errors a browser raises when a lazily imported chunk cannot be
 * fetched. React.lazy caches that rejection, so re-rendering cannot recover;
 * only a page reload can.
 */
export function isChunkLoadError(error: Error | null | undefined): boolean {
  if (!error) return false;
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    error.message ?? '',
  );
}
